// User-facing `<condition-builder-filter>` Knockout component: renders the
// pill, popover, and simple/advanced mode toggle used as a list filter.
// Delegates the nested AND/OR tree editor to `<condition-builder>` (defined in
// ./condition_builder). This is the only symbol screens need to mount as a
// tag filter.

import * as ko from 'knockout';
import i18n from '../i18n';

import { Combinator, RuleGroup, Rule, countRules, fitsSimpleMode } from './condition_builder';
import { IdData } from './list_filters';
import { FormSelectSearchConfiguration } from './form_select_search';
import { translate } from '../i18n_text';

const template = require('raw-loader!../../templates/components/condition_builder_filter.html').default;

export interface ConditionBuilderFilterParams<T extends IdData> {
  group: RuleGroup<T>;
  searchConfig: FormSelectSearchConfiguration<T>;
  label?: string;
  onChange: () => void;
}

type Mode = 'simple' | 'advanced';

interface TagResult<T extends IdData> {
  entity: T;
  id: string;
  name: string;
  selected: ko.Observable<boolean>;
  icon: ko.PureComputed<string>;
  toggle: () => void;
}

/**
 * Kind of token rendered in the filter's inline query preview.
 *
 * - `tag`:           a selected entity chip (tag name).
 * - `operator`:      the AND/OR word placed between sibling tokens.
 * - `parenthesis`:   a `(` or `)` used to visually bracket a nested group.
 */
type PreviewTokenType = 'tag' | 'operator' | 'parenthesis';

interface PreviewToken {
  type: PreviewTokenType;
  cssClass: string;
  text: string;
}

const SEARCH_BAR_THRESHOLD = 9;
const SEARCH_PAGE_LIMIT = 100;

class ConditionBuilderFilter<T extends IdData> {
  group: RuleGroup<T>;
  searchConfig: FormSelectSearchConfiguration<T>;
  onChangeCb: () => void;
  label: string;
  placeholderText: string;

  isOpen = ko.observable(false);
  mode = ko.observable<Mode>('simple');
  advancedDirty = ko.observable(false);

  searchText = ko.observable('').extend({ rateLimit: 200 });
  loading = ko.observable(false);
  hasMoreResults = ko.observable(false);
  searchResults = ko.observableArray<TagResult<T>>([]);
  stagedCombinator = ko.observable<Combinator>(Combinator.Or);

  conditionCount: ko.PureComputed<number>;
  anySelected: ko.PureComputed<boolean>;
  fitsSimple: ko.PureComputed<boolean>;
  searchBarVisible: ko.PureComputed<boolean>;
  stagedSelectionCount: ko.PureComputed<number>;
  combinatorToggleActive: ko.PureComputed<boolean>;
  isAny: ko.PureComputed<boolean>;
  isAll: ko.PureComputed<boolean>;
  queryTokens: ko.PureComputed<PreviewToken[]>;
  combinatorHelpText: string;

  private lastSearchId = 0;
  private subscriptions: ko.Subscription[] = [];
  private rootElement: Element | null;
  private outsideClickHandler: ((event: MouseEvent) => void) | null = null;

  constructor(params: ConditionBuilderFilterParams<T>, rootElement: Element | null = null) {
    this.rootElement = rootElement;
    this.group = params.group;
    this.searchConfig = params.searchConfig;
    this.onChangeCb = params.onChange;
    this.label = params.label ?? i18n.t('Trait Tags')();
    this.placeholderText = i18n.t('Search tags')();

    this.conditionCount = ko.pureComputed(() => countRules(this.group));
    this.anySelected = ko.pureComputed(() => this.conditionCount() > 0);
    this.fitsSimple = ko.pureComputed(() => fitsSimpleMode(this.group));
    this.searchBarVisible = ko.pureComputed(
      () => this.searchResults().length >= SEARCH_BAR_THRESHOLD || this.searchText() !== ''
    );
    this.stagedSelectionCount = ko.pureComputed(
      () => this.searchResults().filter((r) => r.selected()).length
    );
    this.combinatorToggleActive = ko.pureComputed(() => this.stagedSelectionCount() >= 2);
    this.isAny = ko.pureComputed(() => this.stagedCombinator() === Combinator.Or);
    this.isAll = ko.pureComputed(() => this.stagedCombinator() === Combinator.And);
    this.queryTokens = ko.pureComputed(() => this.tokenizeGroup(this.group, false));
    this.combinatorHelpText = i18n.t('Applies when you pick two or more tags')();

    this.subscriptions.push(this.searchText.subscribe(() => this.runSearch()));
    // Keep the simple-mode checkboxes in sync when the rule group is mutated
    // from outside (e.g. the list-filters' "Reset" button clears all rules via
    // the screen's resetAction). Without this, each TagResult keeps its stale
    // `selected` observable, so the next runSearch re-pins them and the user
    // sees the just-reset tags as still checked.
    this.subscriptions.push(this.group.rules.subscribe(() => this.syncSearchResultsSelection()));
  }

  dispose() {
    this.conditionCount.dispose();
    this.anySelected.dispose();
    this.fitsSimple.dispose();
    this.searchBarVisible.dispose();
    this.stagedSelectionCount.dispose();
    this.combinatorToggleActive.dispose();
    this.isAny.dispose();
    this.isAll.dispose();
    this.queryTokens.dispose();
    for (const sub of this.subscriptions) {
      sub.dispose();
    }
    document.removeEventListener('keydown', this.onKeyDown);
  }

  togglePill = () => {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  };

  swallowPopoverClick = () => true;

  open = () => {
    if (this.isOpen()) {
      return;
    }
    this.isOpen(true);
    this.advancedDirty(false);
    this.mode(this.fitsSimple() ? 'simple' : 'advanced');
    this.stagedCombinator(this.group.combinator());
    if (this.mode() === 'simple') {
      this.runSearch();
    }
    document.addEventListener('keydown', this.onKeyDown);
    this.outsideClickHandler = (event: MouseEvent) => {
      if (!this.rootElement) {
        return;
      }
      const target = event.target as HTMLElement;
      if (this.rootElement.contains(target)) {
        return;
      }
      // Modals (Create-tag dialog) sit outside the popover in the DOM.
      if (target.closest('forms-stack')) {
        return;
      }
      this.close();
    };
    setTimeout(() => {
      if (this.outsideClickHandler) {
        document.addEventListener('click', this.outsideClickHandler, true);
      }
    }, 0);
  };

  close = () => {
    if (!this.isOpen()) {
      return;
    }
    const simpleChanged =
      this.mode() === 'simple' ? this.commitSimpleSelection() : false;
    if (simpleChanged || this.advancedDirty()) {
      this.onChangeCb();
    }
    this.advancedDirty(false);
    this.isOpen(false);
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }
  };

  markAdvancedDirty = () => {
    this.advancedDirty(true);
  };

  save = () => {
    this.close();
  };

  reset = () => {
    for (const result of this.searchResults()) {
      result.selected(false);
    }
    this.commitSimpleSelection();
    this.close();
  };

  resetAdvanced = () => {
    if (this.group.rules().length > 0) {
      this.group.rules([]);
      this.advancedDirty(true);
    }
    this.close();
  };

  enterAdvanced = () => {
    if (this.mode() === 'simple' && this.commitSimpleSelection()) {
      this.advancedDirty(true);
    }
    this.mode('advanced');
  };

  requestReturnToSimple = () => {
    if (!this.fitsSimple()) {
      const flatRules: Rule<T>[] = this.group
        .rules()
        .filter((rule): rule is Rule<T> => rule.type === 'rule');
      this.group.rules(flatRules);
      this.advancedDirty(true);
    }
    this.stagedCombinator(this.group.combinator());
    this.mode('simple');
    this.runSearch();
  };

  setSimpleCombinator = (value: Combinator) => {
    this.stagedCombinator(value);
  };

  setSimpleCombinatorAny = () => {
    this.stagedCombinator(Combinator.Or);
  };

  setSimpleCombinatorAll = () => {
    this.stagedCombinator(Combinator.And);
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  };

  runSearch = () => {
    const term = this.searchText().trim().toLocaleLowerCase();
    const searchId = ++this.lastSearchId;
    this.loading(true);
    this.searchConfig
      .list({ offset: 0, limit: SEARCH_PAGE_LIMIT + 1, name_prefix: term })
      .then((results) => {
        if (searchId !== this.lastSearchId) {
          return;
        }
        this.loading(false);
        this.hasMoreResults(results.length > SEARCH_PAGE_LIMIT);
        const sliced = results.slice(0, SEARCH_PAGE_LIMIT);
        const selectedIds = this.selectedIdSet();

        const pinned = this.searchResults().filter((r) => r.selected());
        const pinnedIds = new Set(pinned.map((r) => r.id));

        const tail = sliced
          .filter((entity) => !pinnedIds.has(ko.unwrap(entity.id!) as string))
          .map((entity) => this.makeResult(entity, selectedIds.has(ko.unwrap(entity.id!) as string)));

        this.searchResults(pinned.concat(tail));
      })
      .catch((e) => {
        this.loading(false);
        throw e;
      });
  };

  private tokenizeGroup(
    node: Rule<T> | RuleGroup<T>,
    wrap: boolean
  ): PreviewToken[] {
    if (node.type === 'rule') {
      const name =
        translate(this.searchConfig.getSummaryName(node.entity)) ||
        node.entity.name ||
        '';
      return [{ type: 'tag', cssClass: 'cbf-query-tag', text: name }];
    }
    const opText = node.combinator() === Combinator.And ? 'AND' : 'OR';
    const nonEmptyChildren = node
      .rules()
      .map((child) => this.tokenizeGroup(child, true))
      .filter((tokens) => tokens.length > 0);
    if (nonEmptyChildren.length === 0) {
      return [];
    }
    const tokens: PreviewToken[] = [];
    if (wrap) {
      tokens.push({ type: 'parenthesis', cssClass: 'cbf-query-paren', text: '(' });
    }
    nonEmptyChildren.forEach((childTokens, index) => {
      if (index > 0) {
        tokens.push({ type: 'operator', cssClass: 'cbf-query-op', text: opText });
      }
      tokens.push(...childTokens);
    });
    if (wrap) {
      tokens.push({ type: 'parenthesis', cssClass: 'cbf-query-paren', text: ')' });
    }
    return tokens;
  }

  private syncSearchResultsSelection() {
    const selectedIds = this.selectedIdSet();
    for (const result of this.searchResults()) {
      const shouldBeSelected = selectedIds.has(result.id);
      if (result.selected() !== shouldBeSelected) {
        result.selected(shouldBeSelected);
      }
    }
  }

  private selectedIdSet(): Set<string> {
    const ids = new Set<string>();
    for (const rule of this.group.rules()) {
      if (rule.type === 'rule') {
        ids.add(ko.unwrap(rule.entity.id!) as string);
      }
    }
    return ids;
  }

  private makeResult(entity: T, selected: boolean): TagResult<T> {
    const selectedObs = ko.observable(selected);
    const name = translate(this.searchConfig.getSummaryName(entity)) || entity.name || '';
    const id = ko.unwrap(entity.id!) as string;
    const result: TagResult<T> = {
      entity: entity,
      id: id,
      name: name,
      selected: selectedObs,
      icon: ko.pureComputed<string>(() => (selectedObs() ? 'check_box' : 'check_box_outline_blank')),
      toggle: () => selectedObs(!selectedObs()),
    };
    return result;
  }

  private commitSimpleSelection(): boolean {
    const desired = this.searchResults()
      .filter((r) => r.selected())
      .map((r) => r.entity);

    const currentIds = new Set(
      this.group
        .rules()
        .filter((r): r is Rule<T> => r.type === 'rule')
        .map((r) => ko.unwrap(r.entity.id!) as string)
    );
    const desiredIds = new Set(desired.map((e) => ko.unwrap(e.id!) as string));
    const targetCombinator = this.stagedCombinator();

    const hasNonRule = this.group.rules().some((r) => r.type !== 'rule');
    const sameSet =
      currentIds.size === desiredIds.size &&
      Array.from(desiredIds).every((id) => currentIds.has(id));
    const sameCombinator = this.group.combinator() === targetCombinator;

    if (!hasNonRule && sameSet && sameCombinator) {
      return false;
    }

    this.group.combinator(targetCombinator);
    this.group.rules(desired.map((entity) => ({ type: 'rule' as const, entity: entity })));
    return true;
  }

  static createViewModel<T extends IdData>(
    params: ConditionBuilderFilterParams<T>,
    componentInfo: KnockoutComponentTypes.ComponentInfo
  ) {
    return new ConditionBuilderFilter(params, componentInfo.element as Element);
  }
}

ko.components.register('condition-builder-filter', {
  viewModel: { createViewModel: ConditionBuilderFilter.createViewModel },
  template: template,
});
